import { useEffect, useState } from "react";

/**
 * How the home scroll story is laid out, in one place (StoryGate holds the same height StoryScene will have, so nothing jumps).
 *
 *   pinned   a laptop or bigger (lg up, 520 px tall at least) with motion: ONE phone stays under the header while the steps change, about 0.4 of a screen of
 *            scroll per step. Nothing is scrubbed by the scroll: a step changes the moment its zone crosses the middle of the window (IntersectionObserver)
 *            and the change is a short CSS transition. Native scrolling, no scroll-jacking.
 *   stacked  everywhere else (phones, tablets, reduced motion, data saver): every step is a card with its own phone, in normal page scroll.
 *
 * Pinned lengths are in svh of scroll AFTER the stage has stuck: the first change comes after `lead`, the next ones every `per`, and the last step is held for `hold`.
 */
export const STORY = { lead: 32, per: 50, hold: 30 };
export const PIN_QUERY = "(min-width: 1024px) and (min-height: 520px)";

/** svh of scroll the stage stays pinned for with n steps. */
export const pinnedTravel = (n) => STORY.lead + Math.max(0, n - 2) * STORY.per + STORY.hold;
/** The height of the pinned wrapper: the travel, plus the stage itself (a screen minus the header). */
export const pinnedHeight = (n) => `calc(${pinnedTravel(n)}svh + 100svh - var(--nav-h, 64px))`;
/** Where step i (i >= 1) takes over, in svh of scroll after the stage stuck. */
export const stepAt = (i) => STORY.lead + (i - 1) * STORY.per;

/** true while the window is big enough for the pinned layout. */
export function useWide() {
  const get = () => { try { return window.matchMedia(PIN_QUERY).matches; } catch { return false; } };
  const [wide, setWide] = useState(get);
  useEffect(() => {
    let mq;
    try { mq = window.matchMedia(PIN_QUERY); } catch { return undefined; }
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return wide;
}
